
import { defineComponent, ref, reactive, onMounted, onBeforeUpdate, watch, computed, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import { RepositoryFactory } from '@/repositories/RepositoryFactory'
const SeasonGameRepository = RepositoryFactory.get('seasonGame')

import BaseDialog, { Dialog } from '@/components/BaseDialog.vue'
import CreateMatches from '@/components/seasonGame/CreateMatches.vue'
import RequestGrouping from '@/components/seasonGame/RequestGrouping.vue'

import { Game } from '@/types/game'
import { EntryTeam } from '@/types/entryTeam'

interface LeagueGame {
  id: number
  group_id: number
  match_no?: string
  date?: string
  team_score?: number
  opponent_score?: number
  result_marker?: '○' | '△' | '●'
}

export default defineComponent({
  inheritAttrs: false,
  components: {
    BaseDialog,
    CreateMatches,
    RequestGrouping
  },
  setup() {
    const route = useRoute()
    const store = useStore()

    onMounted(() => {
      getLeagueTable()
    })

    const leagueTable: {
      teams: EntryTeam[]
      games: Game[]
      formedGames: (LeagueGame | null)[][]
      isLoading: boolean
    } = reactive({
      teams: [],
      games: [],
      formedGames: [],
      isLoading: false
    })

    const existLeagueTable = computed(() => {
      return leagueTable.games.length > 0
    })

    const existEntryTeams = computed(() => {
      return leagueTable.teams.length > 1
    })

    const getLeagueTable = async () => {
      try {
        leagueTable.isLoading = true

        const group_id = route.query.group

        const dataObj = {
          group_id: group_id
        }

        const res = await SeasonGameRepository.getGameLeague(dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          leagueTable.teams = data.teams
          leagueTable.games = data.games

          formedLeagueTable()

        }
      } catch (error) {
        //
      } finally {
        leagueTable.isLoading = false
      }
    }

    const formedLeagueTable = () => {
      const teams: EntryTeam[] = leagueTable.teams
      const games: Game[] = leagueTable.games

      leagueTable.formedGames = []
      const formedGames: (LeagueGame | null)[][] = leagueTable.formedGames

      teams.forEach((team, teamIndex: number) => {
        formedGames.push([])

        teams.forEach(opponent => {
          if (team.entry_team_id === opponent.entry_team_id) {
            formedGames[teamIndex].push(null)
          } else {
            const homeGame = games.find((game: Game) => game.home.entry_team_id === team.entry_team_id && game.away.entry_team_id === opponent.entry_team_id)
            const awayGame = games.find((game: Game) => game.home.entry_team_id === opponent.entry_team_id && game.away.entry_team_id === team.entry_team_id)

            let team_score: number | undefined
            let opponent_score: number | undefined
            let result_marker

            if (homeGame != undefined) {
              team_score = homeGame.home_total_score
              opponent_score = homeGame.away_total_score
              result_marker = createResultMarker(team_score, opponent_score)

              const game: LeagueGame = {
                id: homeGame.id,
                group_id: homeGame.group_id,
                match_no: homeGame.match_no,
                date: homeGame.date,
                team_score: team_score,
                opponent_score: opponent_score,
                result_marker: result_marker
              }

              formedGames[teamIndex].push(game)
            } else if (awayGame != undefined) {
              team_score = awayGame.away_total_score
              opponent_score = awayGame.home_total_score
              result_marker = createResultMarker(team_score, opponent_score)

              const game ={
                id: awayGame.id,
                group_id: awayGame.group_id,
                match_no: awayGame.match_no,
                date: awayGame.date,
                team_score: team_score,
                opponent_score: opponent_score,
                result_marker: result_marker
              }

              formedGames[teamIndex].push(game)
            }
          }
        })
      })
    }

    const createResultMarker = (team_score: number | undefined, opponent_score: number | undefined): ('○' | '△' | '●' | undefined) => {
      if (team_score == undefined || opponent_score == undefined) return

      if (team_score > opponent_score) {
        return '○'
      } else if (team_score === opponent_score) {
        return '△'
      } else if (team_score < opponent_score) {
        return '●'
      }
      return
    }

    const createLeagueTable = async () => {
      try {
        const { season_id } = route.params
        const custom_id = route.query.custom
        const group_id = route.query.group

        const dataObj = {
          custome_id: custom_id,
          group_id: group_id
        }

        const res = await SeasonGameRepository.postGameLeague(season_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          leagueTable.games = data.games
          nextTick(() => { formedLeagueTable() })

          store.dispatch('addFlashMessage', {
            type: 'success',
            message: 'リーグ表を作成しました'
          })
        }
      } catch (error) {
        //
      }
    }

    const leagueDialogDelete = reactive<Dialog>({
      title: 'リーグ表を作り直しますか？',
      message: '現在このリーグに登録されている試合の情報は全て削除されます。',
      confirmText: '作り直す',
      cancelText: 'キャンセル',

      isOpen: false
    })

    const openLeagueDialogDelete = () => {
      leagueDialogDelete.isOpen = true
    }

    const closeLeagueDialogDelete = () => {
      leagueDialogDelete.isOpen = false
    }

    const deleteGroupGames = async () => {
      try {
        const { season_id } = route.params
        const group_id = route.query.group

        const dataObj = {
          group_id: group_id
        }

        const res = await SeasonGameRepository.deleteGroupGames(season_id, dataObj)

        if (res.status === 200) {
          const r = res.data
          const data = r.data

          leagueTable.games = data.games

          closeLeagueDialogDelete()
          store.dispatch('addFlashMessage',{
            type: 'success',
            message: 'リーグ表を削除しました'
          })
        }
      } catch (error) {
        //
      }
    }

    return {
      leagueTable,
      existLeagueTable,
      existEntryTeams,
      getLeagueTable,
      createLeagueTable,

      leagueDialogDelete,
      openLeagueDialogDelete,
      closeLeagueDialogDelete,
      deleteGroupGames
    }
  }
})
